// src/pages/Dashboard.js
import React, { useEffect, useState, useCallback } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  BarElement
} from 'chart.js';
import { Pie, Bar } from 'react-chartjs-2';
import Chart from 'react-apexcharts';

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

// Palette de couleurs épurée
const COLORS = {
  primary: '#0B3C5D',
  secondary: '#1E88C8',
  accent: '#10B981',
  warning: '#F59E0B',
  danger: '#DC2626',
  lightBg: '#F8FAFC',
  white: '#FFFFFF',
  darkText: '#2D3748',
  lightText: '#718096',
  chartColors: ['#0B3C5D', '#1E88C8', '#10B981', '#F59E0B', '#DC2626', '#8B5CF6', '#EC4899']
};

// 🎨 Palette NiceAdmin étendue pour ApexCharts (20 couleurs)
const NICEADMIN_COLORS = [
  '#0B3C5D', '#1E88C8', '#10B981', '#F59E0B', '#DC2626', 
  '#8B5CF6', '#EC4899', '#14B8A6', '#F97316', '#6366F1',
  '#84CC16', '#06B6D4', '#D946EF', '#F43F5E', '#7C3AED',
  '#DB2777', '#059669', '#B45309', '#9D174D', '#1E40AF'
];

// 🎨 Palette pour les barres (plus de variété)
const BAR_COLORS = [
  '#0B3C5D', '#1E88C8', '#10B981', '#F59E0B', '#DC2626',
  '#8B5CF6', '#EC4899', '#14B8A6', '#F97316', '#6366F1'
];

// Options communes des graphiques Chart.js
const chartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      position: 'bottom',
      labels: {
        padding: 15,
        usePointStyle: true,
        font: {
          family: 'Inter',
          size: 11
        }
      }
    }
  }
};

// Formatage des nombres
const formatNumber = (num) => {
  if (num === null || num === undefined || isNaN(num)) {
    return "0";
  }
  return new Intl.NumberFormat('fr-FR').format(num);
};

// Formatage des montants avec devise FRANC CFA (toujours complet)
const formatAmount = (amount) => {
  if (amount === null || amount === undefined || isNaN(amount)) {
    return "0 F CFA";
  }
  
  const numAmount = parseFloat(amount);
  if (numAmount === 0) {
    return "0 F CFA";
  }
  
  // Format complet avec séparateurs de milliers
  return `${new Intl.NumberFormat('fr-FR').format(numAmount)} F CFA`;
};

// Calcul des jours restants
const getDaysRemaining = (endDate) => {
  if (!endDate || endDate === "0000-00-00" || endDate === "0000-00-00 00:00:00" || endDate === "NULL") {
    return { days: 0, text: "N/A", isExpired: true, isUrgent: false };
  }
  
  try {
    const end = new Date(endDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diffTime = end - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return {
      days: diffDays,
      text: diffDays > 0 ? `${diffDays} jour${diffDays > 1 ? 's' : ''}` : "Expiré",
      isExpired: diffDays < 0,
      isUrgent: diffDays >= 0 && diffDays <= 7,
      isCritical: diffDays >= 0 && diffDays <= 3
    };
  } catch {
    return { days: 0, text: "N/A", isExpired: true, isUrgent: false, isCritical: false };
  }
};

// Formater la date
const formatDate = (dateString) => {
  if (!dateString || dateString === "0000-00-00" || dateString === "0000-00-00 00:00:00" || dateString === "NULL") {
    return "-";
  }
  try {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  } catch (error) {
    return dateString;
  }
};

// Composant Barre de progression pour abonnement
const ProgressBar = ({ daysRemaining }) => {
  let progress = 100;
  let color = COLORS.accent;
  let textColor = COLORS.darkText;
  
  if (daysRemaining && daysRemaining.days > 0) {
    progress = Math.min(100, (daysRemaining.days / 30) * 100);
    
    if (daysRemaining.isCritical) {
      color = COLORS.danger;
      textColor = COLORS.danger;
    } else if (daysRemaining.isUrgent) {
      color = COLORS.warning;
      textColor = COLORS.warning;
    } else if (daysRemaining.days > 15) {
      color = COLORS.accent;
    } else {
      color = COLORS.secondary;
    }
  } else if (daysRemaining && daysRemaining.isExpired) {
    progress = 0;
    color = COLORS.danger;
    textColor = COLORS.danger;
  }
  
  return (
    <div className="progress-bar-container">
      <div className="d-flex justify-content-between mb-1">
        <small style={{ color: textColor, fontWeight: '500', fontSize: '0.8rem' }}>
          {daysRemaining?.isExpired ? "Expiré" : 
           daysRemaining?.isCritical ? "Critique" :
           daysRemaining?.isUrgent ? "Bientôt expiré" : "Actif"}
        </small>
        <small style={{ color: textColor, fontWeight: '600', fontSize: '0.8rem' }}>
          {daysRemaining?.text}
        </small>
      </div>
      <div className="progress" style={{ height: '6px', borderRadius: '3px' }}>
        <div 
          className="progress-bar" 
          role="progressbar" 
          style={{ 
            width: `${progress}%`,
            backgroundColor: color,
            borderRadius: '3px',
            transition: 'width 0.5s ease'
          }}
          aria-valuenow={progress}
          aria-valuemin="0"
          aria-valuemax="100"
        ></div>
      </div>
    </div>
  );
};

// Composant Carte Statistique compact
const StatCard = ({ title, value, icon, color, subValue, subLabel, bgColor = COLORS.lightBg }) => (
  <div className="d-flex align-items-center p-3 rounded" style={{ 
    backgroundColor: bgColor,
    height: '100%',
    borderLeft: `4px solid ${color}`,
    boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
  }}>
    <div className="me-3">
      <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ 
        width: '48px', 
        height: '48px', 
        backgroundColor: `${color}15`
      }}>
        <i className={`bi ${icon}`} style={{ 
          fontSize: '1.25rem',
          color: color
        }}></i>
      </div>
    </div>
    <div className="flex-grow-1">
      <div style={{ 
        fontSize: '0.8rem',
        color: COLORS.lightText,
        fontWeight: '500',
        marginBottom: '0.2rem'
      }}>
        {title}
      </div>
      <div style={{ 
        fontSize: '1.5rem',
        color: COLORS.darkText,
        fontWeight: '700',
        lineHeight: 1
      }}>
        {formatNumber(value)}
      </div>
      {subValue !== undefined && (
        <div style={{ 
          fontSize: '0.75rem',
          color: color,
          fontWeight: '600',
          marginTop: '0.15rem'
        }}>
          {subLabel ? `${formatNumber(subValue)} ${subLabel}` : formatNumber(subValue)}
        </div>
      )}
    </div>
  </div>
);

// Composant Carte d'alerte d'abonnement
const AbonnementAlertCard = ({ societe, daysRemaining, type, montant }) => (
  <div className="p-3 mb-2 rounded" style={{ 
    backgroundColor: daysRemaining?.isCritical ? '#FEE2E2' : '#FEF3C7',
    border: `1px solid ${daysRemaining?.isCritical ? '#FCA5A5' : '#FCD34D'}`,
    borderRadius: '10px'
  }}>
    <div className="d-flex align-items-center justify-content-between">
      <div>
        <div style={{ fontWeight: '600', fontSize: '0.9rem', color: daysRemaining?.isCritical ? '#991B1B' : '#92400E' }}>
          {societe}
        </div>
        <div className="d-flex align-items-center mt-1">
          <small style={{ color: daysRemaining?.isCritical ? '#DC2626' : '#F59E0B', fontWeight: '600', fontSize: '0.8rem' }}>
            {daysRemaining?.text}
          </small>
          <span className="mx-2" style={{ color: '#D1D5DB' }}>•</span>
          <small style={{ color: '#6B7280', fontSize: '0.8rem' }}>
            {type}
          </small>
        </div>
      </div>
      <div className="text-end">
        <div style={{ fontWeight: '600', fontSize: '0.9rem', color: '#1F2937' }}>
          {formatAmount(montant)}
        </div>
      </div>
    </div>
  </div>
);

// Carte "Validations en attente" : sociétés à valider (admin) ou tâches
// envoyées en validation par les agents (gestionnaire).
const PendingValidationsCard = ({ isAdmin, pendingValidations, formatDate }) => {
  const { count, items, loading } = pendingValidations;
  const title = isAdmin ? "Sociétés en attente de validation" : "Tâches en attente de validation";
  const emptyLabel = isAdmin ? "Aucune inscription en attente" : "Aucune tâche en attente de validation";
  const link = isAdmin ? "/activation" : "/taches";
  const icon = isAdmin ? "bi-building-add" : "bi-clipboard-check";

  if (!loading && count === 0) {
    return null;
  }

  return (
    <div className="col-12 mb-4">
      <div className="card border-0 h-100 sc-elevated" style={{
        borderRadius: '16px',
        borderLeft: `4px solid ${COLORS.warning}`
      }}>
        <div className="card-body">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <div className="d-flex align-items-center">
              <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{
                width: '48px',
                height: '48px',
                backgroundColor: `${COLORS.warning}18`,
                color: COLORS.warning
              }}>
                <i className={`bi ${icon}`} style={{ fontSize: '1.3rem' }}></i>
              </div>
              <div>
                <h5 className="mb-0" style={{ color: COLORS.darkText, fontWeight: '700' }}>
                  {title}
                </h5>
                <small style={{ color: COLORS.lightText }}>
                  Nécessite votre attention
                </small>
              </div>
            </div>
            <span className="sc-pill" style={{ backgroundColor: `${COLORS.warning}18`, color: COLORS.warning }}>
              {loading ? '…' : count}
            </span>
          </div>

          {loading ? (
            <div className="text-center py-3">
              <div className="spinner-border spinner-border-sm" style={{ color: COLORS.warning }} role="status"></div>
            </div>
          ) : count === 0 ? (
            <p className="mb-0 text-center py-2" style={{ color: COLORS.lightText, fontSize: '0.9rem' }}>
              {emptyLabel}
            </p>
          ) : (
            <>
              <div className="d-flex flex-column gap-2">
                {items.map((item, index) => (
                  <Link
                    key={item.id || index}
                    to={link}
                    className="d-flex align-items-center justify-content-between p-2 rounded text-decoration-none"
                    style={{ backgroundColor: COLORS.lightBg }}
                  >
                    <div>
                      <div style={{ fontWeight: '600', fontSize: '0.88rem', color: COLORS.darkText }}>
                        {item.titre}
                      </div>
                      <small style={{ color: COLORS.lightText }}>{item.sousTitre}</small>
                    </div>
                    <small style={{ color: COLORS.lightText, whiteSpace: 'nowrap' }}>
                      {formatDate(item.date)}
                    </small>
                  </Link>
                ))}
              </div>
              {count > items.length && (
                <div className="text-center mt-3">
                  <Link to={link} style={{ color: COLORS.secondary, fontWeight: '600', fontSize: '0.85rem' }}>
                    Voir les {count} demandes <i className="bi bi-arrow-right ms-1"></i>
                  </Link>
                </div>
              )}
              {count > 0 && count <= items.length && (
                <div className="text-center mt-3">
                  <Link to={link} style={{ color: COLORS.secondary, fontWeight: '600', fontSize: '0.85rem' }}>
                    Traiter <i className="bi bi-arrow-right ms-1"></i>
                  </Link>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalClients: 0,
    totalSites: 0,
    totalAgents: 0,
    activeClients: 0,
    activeSites: 0,
    expiringContracts: [],
    sitesWithAgents: []
  });
  
  const [adminStats, setAdminStats] = useState({
    totalSocietes: 0,
    totalAbonnements: 0,
    activeAbonnements: 0,
    expiringAbonnements: 0,
    totalRevenus: 0,
    societesWithAbonnements: [],
    abonnementsByType: {}
  });
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [userInfo, setUserInfo] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showAbonnementModal, setShowAbonnementModal] = useState(false);
  const [abonnementInfo, setAbonnementInfo] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [pendingValidations, setPendingValidations] = useState({ count: 0, items: [], loading: true });

  // Récupérer les infos utilisateur
  useEffect(() => {
    try {
      const stored = localStorage.getItem("user");
      if (stored) {
        const parsed = JSON.parse(stored);
        console.log("User info from localStorage:", parsed);
        setUserInfo(parsed);
        // Vérifier si c'est un administrateur (profil 1)
        const isUserAdmin = parsed.profile === "1" || parsed.profile === 1 || parsed.profil === "1" || parsed.profil === 1;
        console.log("Is user admin?", isUserAdmin, "profile value:", parsed.profile || parsed.profil);
        setIsAdmin(isUserAdmin);
      } else {
        console.log("No user info in localStorage");
        setUserInfo(null);
        setIsAdmin(false);
      }
    } catch (error) {
      console.error("Error parsing user info:", error);
      setUserInfo(null);
      setIsAdmin(false);
    }
  }, []);

  // Charger les statistiques en fonction du profil
  useEffect(() => {
    if (userInfo !== null) { // Attendre que userInfo soit défini (même si null)
      fetchDashboardData();
    }
  }, [userInfo, isAdmin]);

  // Fonction pour récupérer les agents par site
  const fetchAgentsBySite = async (siteId) => {
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        console.error("No token found");
        return [];
      }
      
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.get(
        `${API_BASE_URL}/affectations/agents-by-site.php?id_site=${siteId}`,
        { headers }
      );
      
      if (response.data?.success) {
        return response.data.agents_affectes || [];
      }
      return [];
    } catch (error) {
      console.error(`Erreur lors de la récupération des agents pour le site ${siteId}:`, error);
      return [];
    }
  };

  // Fonction pour récupérer les contrats expirants
  const fetchExpiringContracts = async () => {
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        console.error("No token found");
        return [];
      }
      
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.get(
        `${API_BASE_URL}/clients/list.php`,
        { headers }
      );
      
      if (response.data?.success) {
        const clients = response.data.data?.clients || response.data.clients || [];
        
        // Filtrer les clients actifs dont le contrat expire dans les 15 jours
        return clients
          .filter(client => {
            const daysRemaining = getDaysRemaining(client.date_fin_contrat_client);
            return client.statut_client === 1 && 
                   !daysRemaining.isExpired && 
                   daysRemaining.days <= 15;
          })
          .sort((a, b) => getDaysRemaining(a.date_fin_contrat_client).days - getDaysRemaining(b.date_fin_contrat_client).days);
      }
      return [];
    } catch (error) {
      console.error("Erreur lors de la récupération des contrats expirants:", error);
      return [];
    }
  };

  // Fonction pour récupérer les sites avec leurs agents
  const fetchSitesWithAgents = async () => {
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        console.error("No token found");
        return [];
      }
      
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.get(
        `${API_BASE_URL}/sites/list.php`,
        { headers }
      );
      
      if (response.data?.success) {
        const sites = response.data.data || response.data.sites || [];
        
        // Pour chaque site, récupérer le nombre d'agents
        const sitesWithAgents = [];
        for (const site of sites) {
          try {
            const agentsForSite = await fetchAgentsBySite(site.id_site);
            sitesWithAgents.push({
              ...site,
              agentCount: agentsForSite.length
            });
          } catch (error) {
            console.error(`Erreur pour le site ${site.id_site}:`, error);
            sitesWithAgents.push({
              ...site,
              agentCount: 0
            });
          }
        }
        
        return sitesWithAgents;
      }
      return [];
    } catch (error) {
      console.error("Erreur lors de la récupération des sites:", error);
      return [];
    }
  };

  // Fonction pour récupérer les données d'abonnement de la société connectée
  const fetchSocieteAbonnement = async (societeId) => {
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        console.error("No token found");
        return null;
      }
      
      const headers = { Authorization: `Bearer ${token}` };
      
      // Récupérer tous les abonnements
      const response = await axios.get(
        `${API_BASE_URL}/abonnements/list.php`,
        { headers }
      );
      
      if (response.data?.success) {
        const abonnements = response.data.abonnements || response.data.data || [];
        
        // Filtrer pour avoir uniquement les abonnements de la société connectée
        const societeAbonnements = abonnements.filter(ab => 
          parseInt(ab.id_societe) === parseInt(societeId)
        );
        
        // Trouver l'abonnement actif
        const activeAbonnement = societeAbonnements.find(ab => ab.est_actif == 1 || ab.est_actif === true);
        
        if (activeAbonnement) {
          return {
            ...activeAbonnement,
            daysRemaining: getDaysRemaining(activeAbonnement.date_fin_abonnement)
          };
        }
      }
      return null;
    } catch (error) {
      console.error("Erreur lors de la récupération de l'abonnement:", error);
      return null;
    }
  };

  // Fonction pour récupérer les données admin (sociétés et abonnements)
  const fetchAdminData = async () => {
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setError("Session expirée. Veuillez vous reconnecter.");
        setLoading(false);
        return;
      }
      
      const headers = { Authorization: `Bearer ${token}` };
      
      console.log("Fetching admin data...");
      
      // Récupérer toutes les sociétés (admin peut voir tout)
      const societesRes = await axios.get(
        `${API_BASE_URL}/societes/list.php`,
        { headers }
      );
      
      // Récupérer tous les abonnements
      const abonnementsRes = await axios.get(
        `${API_BASE_URL}/abonnements/list.php`,
        { headers }
      );
      
      let societes = [];
      let abonnements = [];
      
      console.log("Societes response:", societesRes.data);
      console.log("Abonnements response:", abonnementsRes.data);
      
      if (societesRes.data?.success) {
        societes = societesRes.data.societes || societesRes.data.data || [];
      }
      
      if (abonnementsRes.data?.success) {
        abonnements = abonnementsRes.data.abonnements || abonnementsRes.data.data || [];
      }
      
      console.log(`Found ${societes.length} sociétés and ${abonnements.length} abonnements`);
      
      // Calculer les statistiques
      // Un paiement en ligne jamais finalisé n'est pas un abonnement réel —
      // on l'exclut du total pour ne pas gonfler ce chiffre avec des tentatives abandonnées.
      const abonnementsReels = abonnements.filter(a =>
        !(a.mode_paiement === 'en_ligne' && a.statut_paiement === 'pending')
      );
      const totalAbonnements = abonnementsReels.length;
      const activeAbonnements = abonnements.filter(a => a.est_actif == 1 || a.est_actif === true).length;
      
      // Abonnements expirant dans les 15 jours
      const expiringAbonnements = abonnements.filter(ab => {
        const daysRemaining = getDaysRemaining(ab.date_fin_abonnement);
        return (ab.est_actif == 1 || ab.est_actif === true) && !daysRemaining.isExpired && daysRemaining.days <= 15;
      }).length;
      
      // Revenus totaux avec protection contre NaN
      const totalRevenus = abonnements
        .filter(ab => ab.est_actif == 1 || ab.est_actif === true)
        .reduce((sum, ab) => {
          const montant = parseFloat(ab.montant_abonnement) || 0;
          return sum + montant;
        }, 0);
      
      // Grouper les abonnements par société
      const societesWithAbonnements = societes.map(societe => {
        const abonnementsSociete = abonnementsReels.filter(ab =>
          parseInt(ab.id_societe) === parseInt(societe.id_societe)
        );
        const abonnementActif = abonnementsSociete.find(ab => ab.est_actif == 1 || ab.est_actif === true);
        const daysRemaining = abonnementActif ? getDaysRemaining(abonnementActif.date_fin_abonnement) : null;
        
        // Calculer le montant total avec protection
        const montantTotal = abonnementsSociete
          .filter(ab => ab.est_actif == 1 || ab.est_actif === true)
          .reduce((sum, ab) => {
            const montant = parseFloat(ab.montant_abonnement) || 0;
            return sum + montant;
          }, 0);
        
        return {
          ...societe,
          nombreAbonnements: abonnementsSociete.length,
          abonnementActif: abonnementActif ? {
            ...abonnementActif,
            daysRemaining,
            // S'assurer que le montant est un nombre valide
            montant_abonnement: parseFloat(abonnementActif.montant_abonnement) || 0
          } : null,
          montantTotal: montantTotal,
          statutAbonnement: !abonnementActif ? 'non-actif' :
                           daysRemaining?.isExpired ? 'expiré' : 
                           daysRemaining?.isCritical ? 'critique' :
                           daysRemaining?.isUrgent ? 'urgent' : 'actif'
        };
      }).sort((a, b) => b.montantTotal - a.montantTotal);
      
      // Préparer les données pour les graphiques
      const abonnementsByTypeData = {
        'Basique': 0,
        'Standard': 0,
        'Premium': 0,
        'Personnalisé': 0
      };
      
      abonnements.forEach(ab => {
        if (ab.est_actif == 1 || ab.est_actif === true) {
          const type = ab.libele_abonnement || 'Personnalisé';
          abonnementsByTypeData[type] = (abonnementsByTypeData[type] || 0) + 1;
        }
      });
      
      // Filtrer les types qui ont au moins 1 abonnement actif
      const filteredTypes = Object.keys(abonnementsByTypeData).filter(type => abonnementsByTypeData[type] > 0);
      
      const newChartData = {
        abonnementsBarData: {
          labels: filteredTypes,
          datasets: [{
            label: 'Nombre d\'abonnements actifs',
            data: filteredTypes.map(type => abonnementsByTypeData[type]),
            backgroundColor: BAR_COLORS.slice(0, filteredTypes.length),
            borderColor: COLORS.white,
            borderWidth: 2,
            borderRadius: 8,
            barPercentage: 0.7
          }]
        },
        
        societesPieData: {
          labels: societesWithAbonnements
            .filter(s => s.montantTotal > 0)
            .slice(0, 5)
            .map(s => s.nom_societe?.substring(0, 15) || `Société ${s.id_societe}`),
          datasets: [{
            data: societesWithAbonnements
              .filter(s => s.montantTotal > 0)
              .slice(0, 5)
              .map(s => s.montantTotal || 0),
            backgroundColor: NICEADMIN_COLORS.slice(0, Math.min(5, societesWithAbonnements.filter(s => s.montantTotal > 0).length)),
            borderWidth: 2,
            borderColor: COLORS.white
          }]
        }
      };
      
      setAdminStats({
        totalSocietes: societes.length,
        totalAbonnements,
        activeAbonnements,
        expiringAbonnements,
        totalRevenus: totalRevenus || 0, // S'assurer que c'est 0 si NaN
        societesWithAbonnements,
        abonnementsByType: abonnementsByTypeData
      });
      
      setChartData(newChartData);
      
      // Trouver l'abonnement le plus urgent pour l'alerte
      const urgentAbonnements = abonnements
        .filter(ab => ab.est_actif == 1 || ab.est_actif === true)
        .map(ab => ({
          ...ab,
          daysRemaining: getDaysRemaining(ab.date_fin_abonnement),
          // S'assurer que le montant est un nombre valide
          montant_abonnement: parseFloat(ab.montant_abonnement) || 0
        }))
        .filter(ab => ab.daysRemaining.isCritical)
        .sort((a, b) => a.daysRemaining.days - b.daysRemaining.days);
      
      if (urgentAbonnements.length > 0) {
        // Trouver la société correspondante
        const urgentAbonnement = urgentAbonnements[0];
        const societe = societes.find(s => parseInt(s.id_societe) === parseInt(urgentAbonnement.id_societe));
        
        setAbonnementInfo({
          ...urgentAbonnement,
          nom_societe: societe?.nom_societe || "Société inconnue"
        });
        
        const hasShownModal = localStorage.getItem('adminAbonnementAlertShown');
        if (!hasShownModal) {
          setTimeout(() => {
            setShowAbonnementModal(true);
            localStorage.setItem('adminAbonnementAlertShown', 'true');
          }, 2000);
        }
      }
      
      return newChartData;
      
    } catch (error) {
      console.error("Erreur lors de la récupération des données admin:", error);
      if (error.response) {
        console.error("Response data:", error.response.data);
        console.error("Response status:", error.response.status);
      }
      throw error;
    }
  };

  // Fonction pour récupérer les données gestionnaire (sites, clients, agents)
  const fetchGestionnaireData = async (societeId) => {
    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setError("Session expirée. Veuillez vous reconnecter.");
        setLoading(false);
        return;
      }
      
      const headers = { Authorization: `Bearer ${token}` };
      
      console.log("Fetching gestionnaire data for societe ID:", societeId);
      
      // Récupérer les clients
      const clientsRes = await axios.get(
        `${API_BASE_URL}/clients/list.php`,
        { headers }
      );
      
      // Récupérer les sites
      const sitesRes = await axios.get(
        `${API_BASE_URL}/sites/list.php`,
        { headers }
      );
      
      // Récupérer les agents
      const agentsRes = await axios.get(
        `${API_BASE_URL}/agents/list.php`,
        { headers }
      );
      
      let clients = [];
      let sites = [];
      let agents = [];
      
      if (clientsRes.data?.success) {
        clients = clientsRes.data.data?.clients || clientsRes.data.clients || [];
      }
      
      if (sitesRes.data?.success) {
        sites = sitesRes.data.data || sitesRes.data.sites || [];
      }
      
      if (agentsRes.data?.success) {
        agents = agentsRes.data.agents || agentsRes.data.data || [];
      }
      
      // Calculer les statistiques
      const totalClients = clients.length;
      const activeClients = clients.filter(c => c.statut_client == 1 || c.statut_client === true).length;
      
      const totalSites = sites.length;
      const activeSites = sites.filter(s => s.statut_site == 1 || s.statut_site === true).length;
      
      const totalAgents = agents.length;
      
      // Récupérer les contrats expirants
      const expiringContracts = await fetchExpiringContracts();
      
      // Récupérer les sites avec leurs agents
      const sitesWithAgents = await fetchSitesWithAgents();
      
      // Récupérer l'abonnement de la société
      const societeAbonnement = await fetchSocieteAbonnement(societeId);
      
      if (societeAbonnement) {
        // S'assurer que le montant est un nombre valide
        setAbonnementInfo({
          ...societeAbonnement,
          montant_abonnement: parseFloat(societeAbonnement.montant_abonnement) || 0
        });
        
        // Afficher l'alerte si l'abonnement est critique
        if (societeAbonnement.daysRemaining?.isCritical) {
          const hasShownModal = localStorage.getItem('abonnementModalShown');
          if (!hasShownModal) {
            setTimeout(() => {
              setShowAbonnementModal(true);
              localStorage.setItem('abonnementModalShown', 'true');
            }, 2000);
          }
        }
      }
      
      setStats({
        totalClients,
        totalSites,
        totalAgents,
        activeClients,
        activeSites,
        expiringContracts,
        sitesWithAgents
      });
      
      // Préparer les données pour les graphiques
      const sitesWithAgentsData = sitesWithAgents || [];
      
      // Trouver les 5 sites avec le plus d'agents
      const topSites = sitesWithAgentsData
        .sort((a, b) => (b.agentCount || 0) - (a.agentCount || 0))
        .slice(0, 5);
      
      const pieLabels = topSites.map(s => {
        const name = s.nom_site || `Site ${s.id_site}`;
        return name.length > 15 ? name.substring(0, 12) + '...' : name;
      });
      
      const pieData = topSites.map(s => s.agentCount || 0);
      
      const newChartData = {
        agentsBySitePieData: {
          labels: pieLabels,
          datasets: [{
            data: pieData,
            backgroundColor: NICEADMIN_COLORS.slice(0, Math.min(5, topSites.length)),
            borderWidth: 2,
            borderColor: COLORS.white
          }]
        },
        
        sitesAgentsPieData: {
          labels: ['Sites avec agents', 'Sites sans agents', 'Agents total'],
          datasets: [{
            data: [
              sitesWithAgentsData.filter(s => (s.agentCount || 0) > 0).length,
              sitesWithAgentsData.filter(s => (s.agentCount || 0) === 0).length,
              totalAgents || 0
            ],
            backgroundColor: [COLORS.primary, COLORS.lightText, COLORS.accent],
            borderWidth: 2,
            borderColor: COLORS.white
          }]
        }
      };
      
      setChartData(newChartData);
      
      return newChartData;
      
    } catch (error) {
      console.error("Erreur lors de la récupération des données gestionnaire:", error);
      if (error.response) {
        console.error("Response data:", error.response.data);
        console.error("Response status:", error.response.status);
      }
      throw error;
    }
  };

  // Récupère les éléments en attente de validation par l'utilisateur connecté :
  // pour l'admin, les sociétés nouvellement inscrites ; pour le gestionnaire,
  // les tâches que ses agents ont envoyées en validation.
  const fetchPendingValidations = useCallback(async () => {
    const token = localStorage.getItem("access_token");
    if (!token) return;
    const headers = { Authorization: `Bearer ${token}` };

    try {
      if (isAdmin) {
        const response = await axios.get(
          `${API_BASE_URL}/users/activation.php?status=pending`,
          { headers }
        );
        const users = response.data?.data?.users || [];
        setPendingValidations({
          count: users.length,
          items: users.slice(0, 5).map(u => ({
            id: u.id,
            titre: u.societe?.nom || "Société",
            sousTitre: `${u.fullName} · ${u.email}`,
            date: u.created_at
          })),
          loading: false
        });
      } else if ([userInfo?.profile, userInfo?.profil, userInfo?.poste].some(v => v === 3 || v === "3")) {
        const response = await axios.get(
          `${API_BASE_URL}/taches/list.php?statut=en_validation`,
          { headers }
        );
        const taches = response.data?.taches || [];
        setPendingValidations({
          count: taches.length,
          items: taches.slice(0, 5).map(t => ({
            id: t.id_tache,
            titre: t.titre_tache,
            sousTitre: t.agent_info?.nom_complet || "Agent",
            date: t.updated_at
          })),
          loading: false
        });
      } else {
        setPendingValidations({ count: 0, items: [], loading: false });
      }
    } catch (error) {
      console.error("Erreur lors de la récupération des validations en attente:", error);
      setPendingValidations(prev => ({ ...prev, loading: false }));
    }
  }, [isAdmin, userInfo]);

  useEffect(() => {
    if (userInfo !== null) {
      fetchPendingValidations();
    }
  }, [userInfo, isAdmin, fetchPendingValidations]);

  // Fonction principale pour charger les données du dashboard
  const fetchDashboardData = async () => {
    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("access_token");
      if (!token) {
        setError("Session expirée. Veuillez vous reconnecter.");
        setLoading(false);
        return;
      }

      console.log("Fetching dashboard data, isAdmin:", isAdmin);
      
      if (isAdmin) {
        // Admin: voir toutes les sociétés et leurs abonnements
        await fetchAdminData();
      } else {
        // Gestionnaire: voir les données de sa société uniquement
        if (userInfo && userInfo.societe_id) {
          await fetchGestionnaireData(userInfo.societe_id);
        } else if (userInfo && userInfo.id_societe) {
          // Essayer avec id_societe si societe_id n'existe pas
          await fetchGestionnaireData(userInfo.id_societe);
        } else {
          console.log("No societe information in userInfo:", userInfo);
          setError("Impossible de récupérer les informations de la société. Veuillez vous reconnecter.");
        }
      }

    } catch (err) {
      console.error("Erreur dashboard:", err);
      if (err.response?.status === 401) {
        setError("Session expirée. Veuillez vous reconnecter.");
      } else if (err.response?.status === 403) {
        setError("Accès refusé. Vous n'avez pas les permissions nécessaires.");
      } else if (err.code === 'ERR_NETWORK') {
        setError("Erreur de connexion au serveur. Vérifiez votre connexion internet.");
      } else {
        setError("Erreur lors du chargement des données. Veuillez réessayer.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Fermer le modal et réinitialiser l'alerte
  const handleCloseModal = () => {
    setShowAbonnementModal(false);
    if (abonnementInfo?.daysRemaining?.isCritical) {
      setTimeout(() => {
        localStorage.removeItem('abonnementModalShown');
        localStorage.removeItem('adminAbonnementAlertShown');
      }, 300000);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="pagetitle">
          <h1 style={{ color: COLORS.primary, fontWeight: '600', fontSize: '1.8rem' }}>Tableau de bord</h1>
        </div>
        <section className="section">
          <div className="text-center py-5">
            <div className="spinner-border" role="status" style={{ 
              width: '3rem', 
              height: '3rem', 
              color: COLORS.primary 
            }}>
              <span className="visually-hidden">Chargement...</span>
            </div>
            <p className="mt-3" style={{ color: COLORS.darkText, fontSize: '1.1rem' }}>
              Chargement des statistiques...
            </p>
          </div>
        </section>
      </Layout>
    );
  }

  // Le profil agent (4) n'a pas de tableau de bord : les endpoints appelés
  // ci-dessus (clients/sites/agents/list.php) répondent 403 pour ce profil,
  // mais la page continuait à s'afficher derrière le message d'erreur. On
  // redirige vers le profil plutôt que de laisser un tableau de bord vide.
  const agentProfile = userInfo && (userInfo.profile === "4" || userInfo.profile === 4 || userInfo.profil === "4" || userInfo.profil === 4);
  if (agentProfile) {
    return <Navigate to="/profile" replace />;
  }

  // Préparer les données pour les graphiques (fallback si chartData n'est pas défini)
  const displayChartData = chartData || (isAdmin ? {
    abonnementsBarData: {
      labels: ['Basique', 'Standard', 'Premium'],
      datasets: [{
        label: 'Nombre d\'abonnements actifs',
        data: [0, 0, 0],
        backgroundColor: BAR_COLORS.slice(0, 3),
        borderColor: COLORS.white,
        borderWidth: 2,
        borderRadius: 8,
        barPercentage: 0.7
      }]
    },
    societesPieData: {
      labels: ['Aucune donnée'],
      datasets: [{
        data: [1],
        backgroundColor: [COLORS.lightText],
        borderWidth: 2,
        borderColor: COLORS.white
      }]
    }
  } : {
    agentsBySitePieData: {
      labels: ['Aucun site'],
      datasets: [{
        data: [1],
        backgroundColor: [COLORS.lightText],
        borderWidth: 2,
        borderColor: COLORS.white
      }]
    },
    sitesAgentsPieData: {
      labels: ['Sites avec agents', 'Sites sans agents', 'Agents total'],
      datasets: [{
        data: [0, 0, 0],
        backgroundColor: [COLORS.primary, COLORS.lightText, COLORS.accent],
        borderWidth: 2,
        borderColor: COLORS.white
      }]
    }
  });

  return (
    <Layout>
      <div className="pagetitle">
        <h1 style={{ 
          color: COLORS.primary, 
          fontWeight: '700', 
          fontSize: '2rem',
          marginBottom: '0.5rem'
        }}>
          Tableau de bord
        </h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/dashboard" style={{ 
                color: COLORS.primary,
                textDecoration: 'none',
                fontSize: '1.1rem'
              }}>
                <i className="bi bi-house-door me-1"></i>
                Accueil
              </Link>
            </li>
            <li className="breadcrumb-item active" style={{ 
              color: COLORS.secondary,
              fontSize: '1.1rem',
              fontWeight: '500'
            }}>
              Tableau de bord
            </li>
          </ol>
        </nav>
      </div>

      <section className="section dashboard">
        {error && (
          <div className="alert alert-danger alert-dismissible fade show mb-4" style={{ 
            backgroundColor: '#FFF5F5',
            borderColor: '#FEB2B2',
            color: '#C53030',
            borderRadius: '10px',
            fontSize: '1rem',
            padding: '1rem 1.5rem'
          }}>
            <div className="d-flex align-items-center">
              <i className="bi bi-exclamation-triangle-fill me-2" style={{ fontSize: '1.2rem' }}></i>
              <div className="flex-grow-1">{error}</div>
              <button 
                type="button" 
                className="btn-close" 
                onClick={() => setError("")}
                style={{ fontSize: '0.8rem' }}
              ></button>
            </div>
          </div>
        )}

        {/* Vue unique selon le profil */}
        <div className="row">
          {isAdmin ? (
            /* Vue Admin - ABONNEMENTS ET SOCIÉTÉS */
            <>
              <PendingValidationsCard isAdmin={isAdmin} pendingValidations={pendingValidations} formatDate={formatDate} />

              {/* Ligne 1: Statistiques des abonnements */}
              <div className="col-12 mb-4">
                <div className="card border-0 shadow-sm h-100" style={{ 
                  borderRadius: '15px',
                  borderTop: `4px solid ${COLORS.primary}`
                }}>
                  <div className="card-header border-0 bg-white" style={{ 
                    padding: '1.25rem 1.5rem',
                    borderBottom: `2px solid ${COLORS.lightBg}`
                  }}>
                    <h5 className="mb-0" style={{ 
                      color: COLORS.primary,
                      fontWeight: '600',
                      fontSize: '1.1rem'
                    }}>
                      <i className="bi bi-credit-card-2-front me-2"></i>
                      Statistiques des abonnements
                    </h5>
                  </div>
                  <div className="card-body">
                    <div className="row g-3">
                      <div className="col-xl-3 col-lg-3 col-md-6">
                        <StatCard 
                          title="Abonnements actifs"
                          value={adminStats.activeAbonnements}
                          icon="bi-check-circle-fill"
                          color={COLORS.accent}
                          subValue={adminStats.totalAbonnements}
                          subLabel="total"
                        />
                      </div>
                      <div className="col-xl-3 col-lg-3 col-md-6">
                        <StatCard 
                          title="Bientôt expirés"
                          value={adminStats.expiringAbonnements}
                          icon="bi-clock-history"
                          color={COLORS.warning}
                        />
                      </div>
                      <div className="col-xl-3 col-lg-3 col-md-6">
                        <StatCard 
                          title="Revenus/mois"
                          value={adminStats.totalRevenus } 
                          icon="bi-cash"
                          color={COLORS.secondary}
                          subLabel="F CFA"
                        />
                      </div>
                      <div className="col-xl-3 col-lg-3 col-md-6">
                        <StatCard 
                          title="Sociétés"
                          value={adminStats.totalSocietes}
                          icon="bi-buildings"
                          color={COLORS.primary}
                          subValue={adminStats.societesWithAbonnements.filter(s => s.abonnementActif).length}
                          subLabel="avec abonnement"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Ligne 2: PIE CHART ApexCharts + BAR CHART Chart.js */}
              <div className="row">
                {/* Pie Chart ApexCharts - Répartition des revenus par société */}
                <div className="col-xl-6 col-lg-6 col-md-6 mb-4">
                  <div className="card border-0 shadow-sm h-100" style={{ 
                    borderRadius: '15px',
                    borderTop: `4px solid ${COLORS.secondary}`
                  }}>
                    <div className="card-header border-0 bg-white" style={{ 
                      padding: '1.25rem 1.5rem',
                      borderBottom: `2px solid ${COLORS.lightBg}`
                    }}>
                      <h5 className="mb-0" style={{ 
                        color: COLORS.primary,
                        fontWeight: '600',
                        fontSize: '1.1rem'
                      }}>
                        <i className="bi bi-pie-chart-fill me-2"></i>
                        Répartition des revenus par société
                      </h5>
                    </div>
                    <div className="card-body">
                      <div style={{ height: '300px' }}>
                        <Chart
                          options={{
                            chart: {
                              type: 'pie',
                              height: 350,
                              toolbar: { show: false }
                            },
                            labels: displayChartData.societesPieData?.labels || [],
                            colors: NICEADMIN_COLORS,
                            legend: {
                              position: 'bottom',
                              fontSize: '12px',
                              fontFamily: 'Inter, sans-serif',
                              fontWeight: 400,
                              markers: {
                                width: 12,
                                height: 12,
                                radius: 6
                              }
                            },
                            tooltip: {
                              y: {
                                formatter: function(value) {
                                  return formatAmount(value);
                                }
                              }
                            },
                            stroke: {
                              width: 2,
                              colors: ['#fff']
                            }
                          }}
                          series={displayChartData.societesPieData?.datasets[0]?.data || []}
                          type="pie"
                          height={300}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bar Chart Chart.js - Statistiques des abonnements */}
                <div className="col-xl-6 col-lg-6 col-md-6 mb-4">
                  <div className="card border-0 shadow-sm h-100" style={{ 
                    borderRadius: '15px',
                    borderTop: `4px solid ${adminStats.expiringAbonnements > 0 ? COLORS.warning : COLORS.accent}`
                  }}>
                    <div className="card-header border-0 bg-white" style={{ 
                      padding: '1.25rem 1.5rem',
                      borderBottom: `2px solid ${COLORS.lightBg}`
                    }}>
                      <div className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0" style={{ 
                          color: adminStats.expiringAbonnements > 0 ? COLORS.warning : COLORS.darkText,
                          fontWeight: '600'
                        }}>
                          <i className="bi bi-bar-chart-fill me-2"></i>
                          Statistiques des abonnements
                        </h5>
                        <span className="badge" style={{ 
                          backgroundColor: adminStats.expiringAbonnements > 0 ? '#FEF3C7' : '#D1FAE5',
                          color: adminStats.expiringAbonnements > 0 ? '#92400E' : '#065F46',
                          fontWeight: '600',
                          fontSize: '0.8rem'
                        }}>
                          {adminStats.expiringAbonnements}
                        </span>
                      </div>
                    </div>
                    <div className="card-body">
                      <div style={{ height: '250px' }}>
                        <Bar 
                          data={{
                            labels: displayChartData.abonnementsBarData?.labels || [],
                            datasets: [{
                              label: 'Nombre d\'abonnements actifs',
                              data: displayChartData.abonnementsBarData?.datasets[0]?.data || [],
                              backgroundColor: BAR_COLORS,
                              borderColor: '#fff',
                              borderWidth: 2,
                              borderRadius: 8,
                              barPercentage: 0.7
                            }]
                          }}
                          options={{
                            responsive: true,
                            maintainAspectRatio: false,
                            plugins: {
                              legend: { display: false },
                              tooltip: {
                                backgroundColor: COLORS.primary,
                                titleColor: '#fff',
                                bodyColor: '#fff',
                                callbacks: {
                                  label: function(context) {
                                    return `${context.raw} abonnement(s)`;
                                  }
                                }
                              }
                            },
                            scales: {
                              y: {
                                beginAtZero: true,
                                grid: { color: '#E5E7EB', drawBorder: false },
                                ticks: {
                                  stepSize: 1,
                                  color: COLORS.lightText,
                                  font: { size: 11, family: "'Inter', sans-serif" }
                                }
                              },
                              x: {
                                grid: { display: false },
                                ticks: {
                                  color: COLORS.darkText,
                                  font: { size: 12, weight: '500', family: "'Inter', sans-serif" }
                                }
                              }
                            }
                          }}
                        />
                      </div>
                      
                      {/* Légende simplifiée sous le graphique */}
                      <div className="mt-3 d-flex flex-wrap justify-content-center gap-3">
                        {displayChartData.abonnementsBarData?.labels?.map((label, index) => (
                          <div key={index} className="d-flex align-items-center">
                            <span style={{
                              display: 'inline-block',
                              width: '12px',
                              height: '12px',
                              borderRadius: '3px',
                              backgroundColor: BAR_COLORS[index % BAR_COLORS.length],
                              marginRight: '6px'
                            }}></span>
                            <span style={{ fontSize: '0.85rem', color: COLORS.darkText }}>{label}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Ligne 3: Column Chart ApexCharts - Sociétés et leurs abonnements */}
              <div className="col-12 mb-4">
                <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '15px' }}>
                  <div className="card-header border-0 bg-white" style={{ 
                    padding: '1.25rem 1.5rem',
                    borderBottom: `2px solid ${COLORS.lightBg}`
                  }}>
                    <h5 className="mb-0" style={{ 
                      color: COLORS.primary,
                      fontWeight: '600',
                      fontSize: '1.1rem'
                    }}>
                      <i className="bi bi-bar-chart-fill me-2"></i>
                      Sociétés et leurs abonnements
                    </h5>
                  </div>
                  <div className="card-body">
                    <div style={{ height: '400px' }}>
                      <Chart
                        options={{
                          chart: {
                            type: 'bar',
                            height: 350,
                            toolbar: { show: false },
                            stacked: false
                          },
                          plotOptions: {
                            bar: {
                              horizontal: false,
                              columnWidth: '60%',
                              endingShape: 'rounded',
                              borderRadius: 8,
                              distributed: true
                            }
                          },
                          dataLabels: {
                            enabled: false
                          },
                          stroke: {
                            show: true,
                            width: 2,
                            colors: ['#fff']
                          },
                          xaxis: {
                            categories: adminStats.societesWithAbonnements.slice(0, 8).map(s => 
                              s.nom_societe?.substring(0, 15) || `Société ${s.id_societe}`
                            ),
                            labels: {
                              style: {
                                fontSize: '11px',
                                fontFamily: 'Inter, sans-serif',
                                fontWeight: 500
                              },
                              rotate: -45,
                              rotateAlways: true
                            }
                          },
                          yaxis: {
                            title: {
                              text: 'Montant (F CFA)'
                            },
                            labels: {
                              formatter: function(value) {
                                return formatAmount(value);
                              }
                            }
                          },
                          fill: {
                            opacity: 1
                          },
                          colors: NICEADMIN_COLORS,
                          tooltip: {
                            y: {
                              formatter: function(value) {
                                return formatAmount(value);
                              }
                            }
                          },
                          legend: {
                            position: 'bottom',
                            fontSize: '12px',
                            fontFamily: 'Inter, sans-serif',
                            markers: {
                              width: 12,
                              height: 12,
                              radius: 6
                            }
                          }
                        }}
                        series={[
                          {
                            name: 'Montant total',
                            data: adminStats.societesWithAbonnements.slice(0, 8).map(s => s.montantTotal || 0)
                          },
                          {
                            name: 'Abonnements actifs',
                            data: adminStats.societesWithAbonnements.slice(0, 8).map(s => s.nombreAbonnements || 0)
                          }
                        ]}
                        type="bar"
                        height={350}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            /* Vue Gestionnaire - SITES & CLIENTS & AGENTS - AMÉLIORÉE */
            <>
              <PendingValidationsCard isAdmin={isAdmin} pendingValidations={pendingValidations} formatDate={formatDate} />

              {/* Ligne 1: Sites & Clients + Abonnement CÔTE À CÔTE - AMÉLIORÉS */}
              <div className="row">
                {/* Carte Sites & Clients - Version NiceAdmin */}
                <div className="col-xl-6 col-lg-6 col-md-6 mb-4">
                  <div className="card border-0 shadow-sm h-100" style={{ 
                    borderRadius: '15px',
                    borderTop: `4px solid ${COLORS.primary}`
                  }}>
                    <div className="card-header border-0 bg-white" style={{ 
                      padding: '1.25rem 1.5rem',
                      borderBottom: `2px solid ${COLORS.lightBg}`
                    }}>
                      <h5 className="mb-0" style={{ 
                        color: COLORS.primary,
                        fontWeight: '600'
                      }}>
                        <i className="bi bi-building me-2" style={{ color: COLORS.secondary }}></i>
                        Sites & Clients
                      </h5>
                    </div>
                    <div className="card-body">
                      <div className="row g-4">
                        {/* Sites */}
                        <div className="col-6">
                          <div className="text-center p-3 rounded-3" style={{ 
                            backgroundColor: `${COLORS.primary}08`,
                            border: '1px solid #e9ecef'
                          }}>
                            <div className="rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center" style={{ 
                              width: '70px', 
                              height: '70px', 
                              backgroundColor: `${COLORS.primary}20`,
                              color: COLORS.primary
                            }}>
                              <i className="bi bi-buildings" style={{ fontSize: '2rem' }}></i>
                            </div>
                            <h2 style={{ 
                              fontSize: '2.2rem',
                              fontWeight: '700',
                              color: COLORS.primary,
                              marginBottom: '0.25rem'
                            }}>
                              {formatNumber(stats.totalSites)}
                            </h2>
                            <p style={{ 
                              color: COLORS.darkText,
                              fontWeight: '500',
                              fontSize: '0.9rem',
                              marginBottom: '0.5rem'
                            }}>
                              Sites total
                            </p>
                            <div className="d-flex align-items-center justify-content-center">
                              <span className="badge" style={{ 
                                backgroundColor: COLORS.accent + '20',
                                color: COLORS.accent,
                                padding: '0.5rem 1rem',
                                borderRadius: '20px',
                                fontWeight: '600'
                              }}>
                                <i className="bi bi-check-circle me-1"></i>
                                {formatNumber(stats.activeSites)} actifs
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Clients */}
                        <div className="col-6">
                          <div className="text-center p-3 rounded-3" style={{ 
                            backgroundColor: `${COLORS.warning}08`,
                            border: '1px solid #e9ecef'
                          }}>
                            <div className="rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center" style={{ 
                              width: '70px', 
                              height: '70px', 
                              backgroundColor: `${COLORS.warning}20`,
                              color: COLORS.warning
                            }}>
                              <i className="bi bi-people" style={{ fontSize: '2rem' }}></i>
                            </div>
                            <h2 style={{ 
                              fontSize: '2.2rem',
                              fontWeight: '700',
                              color: COLORS.warning,
                              marginBottom: '0.25rem'
                            }}>
                              {formatNumber(stats.totalClients)}
                            </h2>
                            <p style={{ 
                              color: COLORS.darkText,
                              fontWeight: '500',
                              fontSize: '0.9rem',
                              marginBottom: '0.5rem'
                            }}>
                              Clients total
                            </p>
                            <div className="d-flex align-items-center justify-content-center">
                              <span className="badge" style={{ 
                                backgroundColor: COLORS.success + '20',
                                color: COLORS.success,
                                padding: '0.5rem 1rem',
                                borderRadius: '20px',
                                fontWeight: '600'
                              }}>
                                <i className="bi bi-check-circle me-1"></i>
                                {formatNumber(stats.activeClients)} actifs
                              </span>
                            </div>
                          </div>
                        </div>
                        
                        {/* Agents (ligne complète) */}
                        <div className="col-12 mt-2">
                          <div className="p-3 rounded-3 d-flex align-items-center justify-content-between" style={{ 
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            border: 'none'
                          }}>
                            <div className="d-flex align-items-center">
                              <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                                width: '50px', 
                                height: '50px', 
                                backgroundColor: 'rgba(255,255,255,0.2)'
                              }}>
                                <i className="bi bi-shield-check" style={{ fontSize: '1.5rem', color: 'white' }}></i>
                              </div>
                              <div>
                                <div style={{ fontSize: '0.9rem', color: 'rgba(255,255,255,0.8)' }}>
                                  Agents
                                </div>
                                <div style={{ 
                                  fontSize: '2rem',
                                  color: 'white',
                                  fontWeight: '700',
                                  lineHeight: 1
                                }}>
                                  {formatNumber(stats.totalAgents)}
                                </div>
                              </div>
                            </div>
                            <div>
                              <span className="badge" style={{ 
                                backgroundColor: 'rgba(255,255,255,0.2)',
                                color: 'white',
                                padding: '0.5rem 1rem',
                                borderRadius: '20px',
                                fontWeight: '600'
                              }}>
                                <i className="bi bi-check-circle me-1"></i>
                                actifs
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Carte Abonnement - Version NiceAdmin */}
                <div className="col-xl-6 col-lg-6 col-md-6 mb-4">
                  <div className="card border-0 shadow-sm h-100" style={{ 
                    borderRadius: '15px',
                    borderTop: `4px solid ${abonnementInfo?.daysRemaining?.isCritical ? COLORS.danger : 
                                abonnementInfo?.daysRemaining?.isUrgent ? COLORS.warning : COLORS.accent}`
                  }}>
                    <div className="card-header border-0 bg-white" style={{ 
                      padding: '1.25rem 1.5rem',
                      borderBottom: `2px solid ${COLORS.lightBg}`
                    }}>
                      <div className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0" style={{ 
                          color: abonnementInfo?.daysRemaining?.isCritical ? COLORS.danger : 
                                 abonnementInfo?.daysRemaining?.isUrgent ? COLORS.warning : COLORS.accent,
                          fontWeight: '600'
                        }}>
                          <i className="bi bi-credit-card me-2"></i>
                          Abonnement
                        </h5>
                        {abonnementInfo && (
                          <span className="badge" style={{ 
                            backgroundColor: abonnementInfo.daysRemaining?.isCritical ? '#FEE2E2' :
                                            abonnementInfo.daysRemaining?.isUrgent ? '#FEF3C7' : '#D1FAE5',
                            color: abonnementInfo.daysRemaining?.isCritical ? '#DC2626' :
                                   abonnementInfo.daysRemaining?.isUrgent ? '#92400E' : '#065F46',
                            fontWeight: '600',
                            padding: '0.5rem 1rem'
                          }}>
                            {abonnementInfo.daysRemaining?.text}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="card-body">
                      {abonnementInfo ? (
                        <div className="row">
                          {/* Informations principales */}
                          <div className="col-12 mb-4">
                            <div className="d-flex align-items-center">
                              <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                                width: '60px', 
                                height: '60px', 
                                backgroundColor: `${COLORS.accent}15`,
                                color: COLORS.accent
                              }}>
                                <i className="bi bi-trophy" style={{ fontSize: '1.8rem' }}></i>
                              </div>
                              <div>
                                <h4 style={{ fontWeight: '700', color: COLORS.darkText }}>
                                  {abonnementInfo.libele_abonnement}
                                  {abonnementInfo.mode_paiement === 'essai' && (
                                    <span className="badge ms-2" style={{
                                      backgroundColor: '#EDE9FE',
                                      color: '#6D28D9',
                                      fontSize: '0.7rem',
                                      verticalAlign: 'middle'
                                    }}>
                                      <i className="bi bi-stars me-1"></i>ESSAI
                                    </span>
                                  )}
                                </h4>
                                <div className="d-flex align-items-center mt-1">
                                  <span className="badge" style={{
                                    backgroundColor: abonnementInfo.est_actif ? '#D1FAE5' : '#FEE2E2',
                                    color: abonnementInfo.est_actif ? '#065F46' : '#DC2626',
                                    padding: '0.35rem 0.75rem'
                                  }}>
                                    <i className={`bi bi-${abonnementInfo.est_actif ? 'check-circle' : 'x-circle'} me-1`}></i>
                                    {abonnementInfo.est_actif ? 'Actif' : 'Inactif'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          {/* Montant et Date */}
                          <div className="col-md-6 mb-3">
                            <div className="p-3 rounded-3" style={{ backgroundColor: COLORS.lightBg }}>
                              <div style={{ fontSize: '0.9rem', color: COLORS.lightText, marginBottom: '0.5rem' }}>
                                <i className="bi bi-cash-stack me-2"></i>
                                Montant
                              </div>
                              <h3 style={{ fontWeight: '700', color: COLORS.darkText }}>
                                {formatAmount(abonnementInfo.montant_abonnement)}
                              </h3>
                            </div>
                          </div>
                          
                          <div className="col-md-6 mb-3">
                            <div className="p-3 rounded-3" style={{ backgroundColor: COLORS.lightBg }}>
                              <div style={{ fontSize: '0.9rem', color: COLORS.lightText, marginBottom: '0.5rem' }}>
                                <i className="bi bi-calendar me-2"></i>
                                Date d'expiration
                              </div>
                              <h3 style={{ fontWeight: '700', color: COLORS.darkText, fontSize: '1.3rem' }}>
                                {formatDate(abonnementInfo.date_fin_abonnement)}
                              </h3>
                            </div>
                          </div>
                          
                          {/* Barre de progression */}
                          <div className="col-12 mt-2">
                            <ProgressBar daysRemaining={abonnementInfo.daysRemaining} />
                          </div>
                          
                          {/* Boutons d'action — toujours visibles, pas seulement à l'approche de l'expiration */}
                          <div className="col-12 mt-4 d-flex gap-2 flex-wrap">
                            {/* Un essai gratuit ne se "renouvelle" pas — il faut souscrire un
                                premier vrai abonnement payant, pas prolonger l'essai. */}
                            <Link
                              to={abonnementInfo.mode_paiement === 'essai' ? "/abonnements/create" : "/abonnements?renew=true"}
                              className="btn py-3 fw-semibold flex-fill"
                              style={{
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '10px',
                                minWidth: '200px'
                              }}
                            >
                              <i className={`bi ${abonnementInfo.mode_paiement === 'essai' ? 'bi-rocket-takeoff' : 'bi-arrow-clockwise'} me-2`}></i>
                              {abonnementInfo.mode_paiement === 'essai' ? "Souscrire un abonnement" : "Renouveler l'abonnement"}
                            </Link>
                            {/* Achat de quotas non disponible pendant l'essai : quota fixe (2 tags),
                                non extensible par achat — il faut d'abord souscrire un abonnement payant. */}
                            {abonnementInfo.mode_paiement !== 'essai' && (
                              <Link
                                to="/achat-quota?type=tag_qr"
                                className="btn py-3 fw-semibold flex-fill"
                                style={{
                                  backgroundColor: COLORS.white,
                                  color: COLORS.primary,
                                  border: `2px solid ${COLORS.primary}`,
                                  borderRadius: '10px',
                                  minWidth: '200px'
                                }}
                              >
                                <i className="bi bi-tags me-2"></i>
                                Acheter des tags
                              </Link>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="text-center py-5">
                          <div className="rounded-circle mx-auto mb-4 d-flex align-items-center justify-content-center" style={{ 
                            width: '100px', 
                            height: '100px', 
                            backgroundColor: COLORS.lightBg
                          }}>
                            <i className="bi bi-credit-card" style={{ fontSize: '3rem', color: COLORS.lightText }}></i>
                          </div>
                          <h5 style={{ color: COLORS.darkText, fontWeight: '600' }}>
                            Aucun abonnement actif
                          </h5>
                          <p className="mb-4" style={{ color: COLORS.lightText }}>
                            Souscrivez un abonnement pour accéder à toutes les fonctionnalités
                          </p>
                          <Link 
                            to="/abonnements/create" 
                            className="btn fw-semibold px-4 py-2"
                            style={{
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px'
                            }}
                          >
                            <i className="bi bi-plus-circle me-2"></i>
                            Souscrire un abonnement
                          </Link>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Ligne 2: Répartition des agents par site - ApexCharts Pie Chart */}
              <div className="col-xl-8 col-lg-8 col-md-8 mb-4">
                <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '15px' }}>
                  <div className="card-header border-0 bg-white" style={{ 
                    padding: '1.25rem 1.5rem',
                    borderBottom: `2px solid ${COLORS.lightBg}`
                  }}>
                    <h5 className="mb-0" style={{ 
                      color: COLORS.primary,
                      fontWeight: '600'
                    }}>
                      <i className="bi bi-pie-chart-fill me-2"></i>
                      Répartition des agents par site
                    </h5>
                  </div>
                  <div className="card-body">
                    {stats.sitesWithAgents && stats.sitesWithAgents.filter(s => s.agentCount > 0).length > 0 ? (
                      <div style={{ height: '350px' }}>
                        <Chart
                          options={{
                            chart: {
                              type: 'pie',
                              height: 350,
                              toolbar: { show: false }
                            },
                            labels: displayChartData.agentsBySitePieData?.labels || [],
                            colors: NICEADMIN_COLORS,
                            legend: {
                              position: 'bottom',
                              fontSize: '12px',
                              fontFamily: 'Inter, sans-serif',
                              fontWeight: 400,
                              markers: {
                                width: 12,
                                height: 12,
                                radius: 6
                              }
                            },
                            tooltip: {
                              y: {
                                formatter: function(value, { series, seriesIndex, dataPointIndex, w }) {
                                  if (!series || !Array.isArray(series) || series.length === 0) {
                                    return `${value} agent(s)`;
                                  }
                                  const total = series.reduce((a, b) => a + b, 0);
                                  const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
                                  return `${value} agent(s) (${percentage}%)`;
                                }
                              }
                            },
                            stroke: {
                              width: 2,
                              colors: ['#fff']
                            }
                          }}
                          series={displayChartData.agentsBySitePieData?.datasets[0]?.data || []}
                          type="pie"
                          height={350}
                        />
                      </div>
                    ) : (
                      <div className="text-center py-5">
                        <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto mb-3" style={{ 
                          width: '80px',
                          height: '80px',
                          backgroundColor: COLORS.lightBg,
                          color: COLORS.lightText
                        }}>
                          <i className="bi bi-pie-chart" style={{ fontSize: '2rem' }}></i>
                        </div>
                        <h6 style={{ color: COLORS.darkText, fontWeight: '600' }}>
                          Aucun agent assigné
                        </h6>
                        <p className="mb-0" style={{ color: COLORS.lightText }}>
                          Les statistiques apparaîtront ici une fois que des agents seront affectés aux sites.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Ligne 2: Contrats à renouveler - Version NiceAdmin */}
              <div className="col-xl-4 col-lg-4 col-md-4 mb-4">
                <div className="card border-0 shadow-sm h-100" style={{ 
                  borderRadius: '15px',
                  borderTop: `4px solid ${stats.expiringContracts.length > 0 ? COLORS.warning : COLORS.accent}`
                }}>
                  <div className="card-header border-0 bg-white" style={{ 
                    padding: '1.25rem 1.5rem',
                    borderBottom: `2px solid ${COLORS.lightBg}`
                  }}>
                    <div className="d-flex justify-content-between align-items-center">
                      <h5 className="mb-0" style={{ 
                        color: stats.expiringContracts.length > 0 ? COLORS.warning : COLORS.darkText,
                        fontWeight: '600'
                      }}>
                        <i className="bi bi-calendar-exclamation me-2"></i>
                        Contrats à renouveler
                      </h5>
                      <span className="badge rounded-pill" style={{ 
                        backgroundColor: stats.expiringContracts.length > 0 ? COLORS.warning : COLORS.accent,
                        color: 'white',
                        fontWeight: '600',
                        fontSize: '1rem',
                        padding: '0.5rem 1rem'
                      }}>
                        {stats.expiringContracts.length}
                      </span>
                    </div>
                  </div>
                  <div className="card-body">
                    {stats.expiringContracts.length > 0 ? (
                      <>
                        {/* Contrat le plus urgent */}
                        <div className="alert mb-4" style={{ 
                          backgroundColor: getDaysRemaining(stats.expiringContracts[0]?.date_fin_contrat_client).isCritical ? '#FEE2E2' : '#FEF3C7',
                          border: 'none',
                          borderRadius: '10px',
                          padding: '1rem'
                        }}>
                          <div className="d-flex align-items-center">
                            <div className="rounded-circle d-flex align-items-center justify-content-center me-3" style={{ 
                              width: '48px', 
                              height: '48px', 
                              backgroundColor: getDaysRemaining(stats.expiringContracts[0]?.date_fin_contrat_client).isCritical ? COLORS.danger : COLORS.warning,
                              color: 'white'
                            }}>
                              <i className={`bi ${getDaysRemaining(stats.expiringContracts[0]?.date_fin_contrat_client).isCritical ? 'bi-exclamation-triangle' : 'bi-exclamation'}`} 
                                 style={{ fontSize: '1.5rem' }}></i>
                            </div>
                            <div>
                              <div style={{ fontWeight: '600', color: getDaysRemaining(stats.expiringContracts[0]?.date_fin_contrat_client).isCritical ? '#991B1B' : '#92400E' }}>
                                {stats.expiringContracts[0]?.nom_client}
                              </div>
                              <div className="d-flex align-items-center mt-1">
                                <span className="badge me-2" style={{ 
                                  backgroundColor: getDaysRemaining(stats.expiringContracts[0]?.date_fin_contrat_client).isCritical ? '#FEE2E2' : '#FEF3C7',
                                  color: getDaysRemaining(stats.expiringContracts[0]?.date_fin_contrat_client).isCritical ? '#DC2626' : '#92400E',
                                  fontWeight: '600',
                                  padding: '0.35rem 0.75rem'
                                }}>
                                  {getDaysRemaining(stats.expiringContracts[0]?.date_fin_contrat_client).text}
                                </span>
                                <small style={{ color: '#6B7280' }}>
                                  {formatDate(stats.expiringContracts[0]?.date_fin_contrat_client)}
                                </small>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Liste des autres contrats */}
                        <div className="list-group list-group-flush">
                          {stats.expiringContracts.slice(1, 4).map((client, index) => {
                            const daysRemaining = getDaysRemaining(client.date_fin_contrat_client);
                            return (
                              <div key={index} className="list-group-item border-0 px-0 py-3">
                                <div className="d-flex align-items-center">
                                  <div className="flex-shrink-0 me-3">
                                    <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ 
                                      width: '40px', 
                                      height: '40px', 
                                      backgroundColor: daysRemaining.isCritical ? '#FEE2E2' :
                                                       daysRemaining.isUrgent ? '#FEF3C7' : '#E0F2FE',
                                      color: daysRemaining.isCritical ? '#DC2626' :
                                             daysRemaining.isUrgent ? '#92400E' : '#0369A1'
                                    }}>
                                      <i className={`bi ${
                                        daysRemaining.isCritical ? 'bi-exclamation-triangle-fill' :
                                        daysRemaining.isUrgent ? 'bi-exclamation' : 'bi-calendar'
                                      }`}></i>
                                    </div>
                                  </div>
                                  <div className="flex-grow-1">
                                    <div className="d-flex justify-content-between align-items-center">
                                      <div>
                                        <div style={{ fontWeight: '500', fontSize: '0.95rem' }}>
                                          {client.nom_client} {client.prenom_client?.charAt(0)}.
                                        </div>
                                        <small className="text-muted" style={{ fontSize: '0.8rem' }}>
                                          {formatDate(client.date_fin_contrat_client)}
                                        </small>
                                      </div>
                                      <span className="badge" style={{ 
                                        backgroundColor: daysRemaining.isCritical ? '#FEE2E2' :
                                                         daysRemaining.isUrgent ? '#FEF3C7' : '#E0F2FE',
                                        color: daysRemaining.isCritical ? '#DC2626' :
                                               daysRemaining.isUrgent ? '#92400E' : '#0369A1',
                                        fontWeight: '600',
                                        padding: '0.35rem 0.75rem'
                                      }}>
                                        {daysRemaining.text}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Lien vers la liste complète */}
                        {stats.expiringContracts.length > 4 && (
                          <div className="text-center mt-3">
                            <Link 
                              to="/clients?filter=expiring" 
                              className="btn btn-link fw-semibold"
                              style={{
                                color: COLORS.primary,
                                textDecoration: 'none'
                              }}
                            >
                              Voir tous les contrats ({stats.expiringContracts.length})
                              <i className="bi bi-arrow-right ms-1"></i>
                            </Link>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-center py-5">
                        <div className="rounded-circle mx-auto mb-4 d-flex align-items-center justify-content-center" style={{ 
                          width: '100px', 
                          height: '100px', 
                          backgroundColor: COLORS.lightBg
                        }}>
                          <i className="bi bi-check-circle" style={{ fontSize: '3rem', color: COLORS.accent }}></i>
                        </div>
                        <h6 style={{ color: COLORS.darkText, fontWeight: '600' }}>
                          Aucun contrat à renouveler
                        </h6>
                        <p className="mb-0" style={{ color: COLORS.lightText, fontSize: '0.9rem' }}>
                          Tous les contrats sont à jour
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      {/* Modal d'alerte abonnement */}
      {showAbonnementModal && abonnementInfo && (
        <div 
          className="modal fade show" 
          style={{ 
            display: 'block', 
            backgroundColor: 'rgba(0,0,0,0.5)',
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 1050
          }}
        >
          <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '500px' }}>
            <div className="modal-content" style={{ 
              borderRadius: '15px', 
              border: 'none',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
            }}>
              <div className="modal-header border-0" style={{ 
                backgroundColor: abonnementInfo.daysRemaining?.isCritical ? '#FEE2E2' : '#FEF3C7',
                borderTopLeftRadius: '15px',
                borderTopRightRadius: '15px'
              }}>
                <div className="d-flex align-items-center w-100">
                  <i className="bi bi-exclamation-triangle-fill me-2" style={{ 
                    fontSize: '1.5rem',
                    color: abonnementInfo.daysRemaining?.isCritical ? '#DC2626' : '#F59E0B'
                  }}></i>
                  <h5 className="modal-title mb-0" style={{ 
                    color: abonnementInfo.daysRemaining?.isCritical ? '#991B1B' : '#92400E',
                    fontWeight: '600'
                  }}>
                    Alerte {abonnementInfo.daysRemaining?.isCritical ? 'CRITIQUE' : 'Important'} 
                  </h5>
                </div>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={handleCloseModal}
                ></button>
              </div>
              <div className="modal-body py-4">
                <div className="text-center mb-4">
                  <div style={{ 
                    fontSize: '5rem',
                    color: abonnementInfo.daysRemaining?.isCritical ? '#DC2626' : '#F59E0B',
                    fontWeight: '700',
                    lineHeight: 1
                  }}>
                    {abonnementInfo.daysRemaining?.days || 0}
                  </div>
                  <div style={{ 
                    color: abonnementInfo.daysRemaining?.isCritical ? '#991B1B' : '#92400E',
                    fontWeight: '600',
                    fontSize: '1.2rem'
                  }}>
                    jours restants
                  </div>
                </div>
                
                <div className="alert" style={{ 
                  backgroundColor: '#F8FAFC',
                  border: '1px solid #E5E7EB',
                  borderRadius: '10px'
                }}>
                  <div className="d-flex align-items-start">
                    <i className="bi bi-info-circle me-3" style={{ 
                      color: abonnementInfo.daysRemaining?.isCritical ? '#DC2626' : '#F59E0B'
                    }}></i>
                    <div>
                      <p className="mb-2" style={{ fontWeight: '500' }}>
                        {isAdmin ? (
                          <>L'abonnement de <strong>{abonnementInfo.nom_societe}</strong> expire bientôt.</>
                        ) : (
                          <>Votre abonnement <strong>{abonnementInfo.libele_abonnement}</strong> expire bientôt.</>
                        )}
                      </p>
                      <p className="mb-0" style={{ fontSize: '0.9rem' }}>
                        Date d'expiration : <strong>{formatDate(abonnementInfo.date_fin_abonnement)}</strong>
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer border-0">
                <button 
                  type="button" 
                  className="btn" 
                  onClick={handleCloseModal}
                  style={{ 
                    backgroundColor: '#F1F5F9',
                    color: COLORS.darkText,
                    fontWeight: '500',
                    border: 'none'
                  }}
                >
                  Plus tard
                </button>
                <Link 
                  to={isAdmin ? "/abonnements" : "/abonnements?renew=true"}
                  className="btn"
                  style={{ 
                    backgroundColor: abonnementInfo.daysRemaining?.isCritical ? '#DC2626' : '#F59E0B',
                    color: 'white',
                    fontWeight: '500',
                    border: 'none'
                  }}
                  onClick={handleCloseModal}
                >
                  <i className="bi bi-arrow-clockwise me-2"></i>
                  {isAdmin ? "Voir les abonnements" : "Renouveler maintenant"}
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Styles améliorés */}
      <style>{`
        * {
          font-family: 'Inter', sans-serif;
        }
        
        .pagetitle h1 {
          font-size: 2rem !important;
          font-weight: 700 !important;
        }
        
        .card {
          transition: all 0.3s ease !important;
          border: 1px solid #E5E7EB !important;
        }
        
        .card:hover {
          transform: translateY(-5px) !important;
          box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1) !important;
        }
        
        .badge {
          font-weight: 600 !important;
          letter-spacing: 0.3px;
          padding: 0.4rem 0.75rem !important;
        }
        
        .list-group-item:hover {
          background-color: ${COLORS.lightBg} !important;
        }
        
        .btn {
          font-weight: 500 !important;
          transition: all 0.2s ease !important;
          border-radius: 8px !important;
        }
        
        .btn:hover {
          transform: translateY(-1px) !important;
          box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1) !important;
        }
        
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        .card {
          animation: fadeIn 0.5s ease-out !important;
        }
        
        /* Animation pulsante pour la carte d'abonnement critique */
        @keyframes pulseCritical {
          0% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.4); }
          70% { box-shadow: 0 0 0 10px rgba(220, 38, 38, 0); }
          100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0); }
        }
        
        .card[style*="border-top: 4px solid #DC2626"] {
          animation: fadeIn 0.5s ease-out, pulseCritical 2s infinite !important;
        }
        
        /* Responsive */
        @media (max-width: 768px) {
          .pagetitle h1 {
            font-size: 1.5rem !important;
          }
          
          .card {
            margin-bottom: 1rem !important;
          }
          
          /* Ajustement pour la disposition côte à côte sur mobile */
          .row > .col-xl-8,
          .row > .col-lg-8,
          .row > .col-md-8,
          .row > .col-xl-4,
          .row > .col-lg-4,
          .row > .col-md-4 {
            width: 100% !important;
            margin-bottom: 1rem;
          }
          
          /* Ajustement pour les cartes admin en 2 colonnes */
          .row > .col-xl-6,
          .row > .col-lg-6,
          .row > .col-md-6 {
            width: 100% !important;
            margin-bottom: 1rem;
          }
          
          .card-body .display-1 {
            font-size: 3rem !important;
          }
          
          /* Graphiques sur mobile */
          .card-body div[style*="height: 350px"],
          .card-body div[style*="height: 300px"] {
            height: 250px !important;
          }
        }
        
        @media (max-width: 576px) {
          .pagetitle h1 {
            font-size: 1.3rem !important;
          }
          
          .card-body {
            padding: 1rem !important;
          }
          
          .btn {
            padding: 0.5rem 1rem !important;
            font-size: 0.9rem !important;
          }
          
          h5 {
            font-size: 1rem !important;
          }
          
          /* Ajustement pour les graphiques sur mobile */
          .card-body div[style*="height: 350px"] {
            height: 200px !important;
          }
          
          .card-body div[style*="height: 300px"] {
            height: 180px !important;
          }
          
          /* Table responsive */
          .table-responsive {
            font-size: 0.85rem !important;
          }
        }
        
        /* Amélioration des graphiques */
        canvas {
          max-height: 300px !important;
        }
        
        /* Scroll personnalisé pour les listes */
        div[style*="max-height: 200px"]::-webkit-scrollbar {
          width: 5px;
        }
        
        div[style*="max-height: 200px"]::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 10px;
        }
        
        div[style*="max-height: 200px"]::-webkit-scrollbar-thumb {
          background: ${COLORS.lightText};
          border-radius: 10px;
        }
        
        div[style*="max-height: 200px"]::-webkit-scrollbar-thumb:hover {
          background: ${COLORS.primary};
        }
        
        /* Animation de chargement */
        @keyframes shimmer {
          0% { background-position: -468px 0; }
          100% { background-position: 468px 0; }
        }
        
        .loading-shimmer {
          background: linear-gradient(to right, #f6f7f8 0%, #edeef1 20%, #f6f7f8 40%, #f6f7f8 100%);
          background-size: 800px 104px;
          animation: shimmer 1.2s infinite linear;
        }
      `}</style>
    </Layout>
  );
};

export default Dashboard;